import axiosIns from "@/libs/axios";

export default {
  namespaced: true,
  state: {},
  getters: {},
  mutations: {},
  actions: {
    // Events
    fetchEvents(ctx, params) {
      return axiosIns.get("events", { params });
    },
    fetchEventsByYear(ctx, year) {
      return axiosIns.get(`events/year/${year}`);
    },
    fetchEventsAllYears(ctx, params = {}) {
      // Back-end now uses GET /api/events (optionally with year/per_page).
      // Keep this action name for callers that still dispatch fetchEventsAllYears.
      return axiosIns.get("events/year", { params });
    },
    saveEvent(ctx, data) {
      return axiosIns.post("saveEvent", data);
    },
    getEventById(ctx, { id }) {
      // Primary: GET /api/events/{id}
      // Fallback: legacy route kept for compatibility in older deployments
      return axiosIns.get(`getEventById/${id}`);
    },
    updateEvent(ctx, { id, data }) {
      return axiosIns.put(`updateEvent/${id}`, data);
    },
    deleteEvent(ctx, { id }) {
      return axiosIns.delete(`deleteEvent/${id}`);
    },
    setEventAdmins(ctx, { id, admin_user_ids }) {
      return axiosIns.post(`events/${id}/admins`, { admin_user_ids });
    },

    // Sessions
    listSessions(ctx, { id }) {
      return axiosIns.get(`events/${id}/sessions`);
    },
    getSessionById(ctx, { sessionId }) {
      return axiosIns.get(`event-sessions/${sessionId}`);
    },
    addSession(ctx, { id, data }) {
      return axiosIns.post(`events/${id}/sessions`, data);
    },
    updateSession(ctx, { sessionId, data }) {
      return axiosIns.put(`event-sessions/${sessionId}`, data);
    },
    deleteSession(ctx, { sessionId }) {
      return axiosIns.delete(`event-sessions/${sessionId}`);
    },

    // Applications
    listApplications(ctx, { id }) {
      return axiosIns.get(`events/${id}/applications`);
    },
    createApplication(ctx, { id, data }) {
      return axiosIns.post(`events/${id}/applications`, data);
    },
    submitApplication(ctx, { applicationId, data } = {}) {
      return axiosIns.post(`applications/${applicationId}/submit`, data || {});
    },
    managerApprove(ctx, { applicationId, comment }) {
      return axiosIns.post(`applications/${applicationId}/manager-approve`, {
        comment,
      });
    },
    managerReject(ctx, { applicationId, comment }) {
      return axiosIns.post(`applications/${applicationId}/manager-reject`, {
        comment,
      });
    },
    adminAccept(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/admin-accept`);
    },
    adminReject(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/admin-reject`);
    },
    adminWaitlist(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/waitlist`);
    },
    withdrawApplication(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/withdraw`);
    },
    cancelApplication(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/cancel`);
    },
    getUserApplications(ctx) {
      return axiosIns.get("dashboard/applications");
    },
    getAllApplications(ctx, { year }) {
      return axiosIns.get(`dashboard/all-applications?year=${year}`);
    },
    // Manager approval actions
    managerApproveApplication(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/manager-approve`);
    },
    managerRejectApplication(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/manager-reject`);
    },
    // Admin approval actions (existing ones from EventApplications)
    adminAcceptApplication(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/admin-accept`);
    },
    adminRejectApplication(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/admin-reject`);
    },
    adminWaitlistApplication(ctx, { applicationId }) {
      return axiosIns.post(`applications/${applicationId}/waitlist`);
    },
    getCourseById(ctx, { id }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`getCourseById/${id}`)
          .then((r) => resolve(r))
          .catch((e) => reject(e));
      });
    },

    // Attendance
    listAttendanceBySession(ctx, { sessionId }) {
      return axiosIns.get(`event-sessions/${sessionId}/attendance`);
    },
    sessionAudienceSummary(ctx, { sessionId }) {
      return axiosIns.get(`event-sessions/${sessionId}/audience-summary`);
    },
    exportAttendanceBySession(ctx, { sessionId }) {
      return axiosIns.get(`event-sessions/${sessionId}/attendance/export`, { responseType: 'blob' });
    },
    addAttendanceRecord(ctx, { sessionId, data }) {
      return axiosIns.post(`event-sessions/${sessionId}/attendance`, data);
    },
    updateAttendanceRecord(ctx, { recordId, data }) {
      return axiosIns.put(`attendance/${recordId}`, data);
    },
    deleteAttendanceRecord(ctx, { recordId }) {
      return axiosIns.delete(`attendance/${recordId}`);
    },
    attendanceByUser(ctx, { userId }) {
      return axiosIns.get(`attendance/user/${userId}`);
    },
    attendanceByEvent(ctx, { eventId }) {
      return axiosIns.get(`attendance/event/${eventId}`);
    },

    // Options
    fetchEventOptions() {
      return axiosIns.get("events/options");
    },

    // Audience preview
    previewAudience(ctx, payload) {
      return axiosIns.post("events/audience/preview", payload);
    },

    // Materials
    listMaterials(ctx, { id }) {
      return axiosIns.get(`events/${id}/materials`);
    },
    uploadMaterial(ctx, { id, data }) {
      return axiosIns.post(`events/${id}/materials`, data);
    },
    // Upload a material before an event exists (detached)
    uploadDetachedMaterial(ctx, data) {
      return axiosIns.post(`materials/detached`, data);
    },
    updateMaterial(ctx, { materialId, data }) {
      return axiosIns.post(`materials/${materialId}`, data);
    },
    deleteMaterial(ctx, { materialId }) {
      return axiosIns.delete(`materials/${materialId}`);
    },

    // Invitations
    sendInvitations(ctx, { id }) {
      return axiosIns.post(`events/${id}/send-invitations`);
    },
    sendNewInvitations(ctx, { id }) {
      return axiosIns.post(`events/${id}/send-new-invitations`);
    },
    listApplicationInvites(ctx, { id }) {
      return axiosIns.get(`events/${id}/application-invites`);
    },

    // Dashboard filter options
    fetchDashboardOptions() {
      return axiosIns.get("dashboard/options");
    },

    // Reminders
    sendReminderToManager(ctx, { applicationId, managerId, message }) {
      return axiosIns.post("applications/send-reminder", {
        application_id: applicationId,
        manager_id: managerId,
        message: message,
      });
    },
  },
};
